import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useMutation } from '@apollo/client';
import { Button } from 'react-bootstrap';
import PropdocsModal from '../modal/PropdocsModal';
import { publishTemplateChanges, saveDocumentVersion } from '../../api/mutations';
import { useNotifications } from '../../notifications';
import { TemplateType } from '../../consts/forms-types';
import './PublishTemplateModal.scss';

interface PublishTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  templateId: string;
  templateName: string;
  templateType?: TemplateType;
  hasPendingSuggestions: boolean;
  onPublishSuccess: () => void;
  // V4.0: DOCX-native templates publish by promoting the autosaved draft (which
  // is already the source of truth) — no CKEditor global / RevisionTracker /
  // HTML comment-stripping. When true, handlePublish takes the native path.
  isDocxNative?: boolean;
}

export const PublishTemplateModal: React.FC<PublishTemplateModalProps> = ({
  isOpen,
  onClose,
  templateId,
  templateName,
  templateType,
  hasPendingSuggestions,
  onPublishSuccess,
  isDocxNative = false,
}) => {
  const [isPublishing, setIsPublishing] = useState(false);
  const [, { success: notifySuccess, error: notifyError }] = useNotifications();
  const [publishChanges] = useMutation(publishTemplateChanges);
  const [saveTemplateVersion] = useMutation(saveDocumentVersion);

  const handleClose = () => {
    if (isPublishing) return; // Prevent closing while publishing
    onClose();
  };

  const handlePublish = async () => {
    if (isPublishing || hasPendingSuggestions) return;

    setIsPublishing(true);

    try {
      // Determine if this is a clause or template
      const isClauseType = templateType === TemplateType.CLAUSE;

      // ── DOCX-native path ──────────────────────────────────────────────────
      // The draft is already the source of truth (autosaved as a .docx/model
      // body with no CKEditor comment markup). Publishing just promotes the
      // draft server-side — no window.editor, no RevisionTracker, no HTML
      // strip. `clean_content` is omitted so the backend copies the draft.
      if (isDocxNative) {
        // Snapshot a named "Published – {date}" version of the draft BEFORE it
        // is promoted, so publishes appear in version history (the docx-native
        // draft is already autosaved, so it is the exact published content). A
        // failed snapshot must not block the publish — publishing is the
        // primary action — but the user is warned history wasn't captured.
        const publishedVersionName = `Published – ${new Date().toLocaleString()}`;
        try {
          await saveTemplateVersion({
            variables: {
              documentId: templateId,
              documentType: 'DocumentTemplate',
              name: publishedVersionName,
              event: 'publish',
            },
          });
        } catch {
          notifyError({
            message: 'Published, but could not save a version snapshot for history.',
          });
        }

        const result = await publishChanges({
          variables: { documentTemplateId: templateId },
          update: (cache, { data }) => {
            if (data?.publishTemplateChanges?.documentTemplate) {
              cache.modify({
                id: cache.identify({ __typename: 'DocumentTemplate', id: templateId }),
                fields: { hasPendingChanges: () => false },
              });
            }
          },
        });
        if (!result?.data?.publishTemplateChanges) {
          throw new Error('Publish operation returned no data. Please try again.');
        }
        const nativeErrors = result.data.publishTemplateChanges.errors || [];
        if (nativeErrors.length > 0) {
          throw new Error(`Publish failed: ${nativeErrors.join(', ')}`);
        }
        notifySuccess({
          message: `${isClauseType ? 'Clause' : 'Template'} published successfully!`,
        });
        onPublishSuccess();
        onClose();
        return;
      }

      // Anything that is not docx-native was authored in the retired CKEditor.
      // The publish path for those bodies drove `window.editor` + its
      // RevisionTracker plugin, which no longer exist — it could only ever
      // throw "editor is not ready". Fail with something the user can act on.
      throw new Error(
        `This ${isClauseType ? 'clause' : 'template'} was authored in the previous editor and must be migrated before it can be published.`
      );

    } catch (error) {
      console.error('❌ Publish failed:', error);

      // Clean error messages based on error type (All-or-Nothing approach)
      let userMessage = `An unexpected error occurred while publishing the ${templateType === TemplateType.CLAUSE ? 'clause' : 'template'}. Please try again.`;

      if (error instanceof Error) {
        // Use the specific error message if it's user-friendly
        if (
          error.message.includes('editor is not ready') ||
          error.message.includes('Failed to save pending edits') ||
          error.message.includes('Failed to remove comments') ||
          error.message.includes('Failed to create revision') ||
          error.message.includes('Failed to publish') ||
          error.message.includes('content is empty') ||
          error.message.includes('Publish failed:')
        ) {
          userMessage = error.message;
        }
      }

      notifyError({ message: userMessage });
    } finally {
      setIsPublishing(false);
    }
  };

  const getModalTitle = () => {
    const isClause = templateType === TemplateType.CLAUSE;

    if (hasPendingSuggestions) {
      return (
        <FormattedMessage
          id={isClause ? 'clause.publish.blocked.title' : 'template.publish.blocked.title'}
          defaultMessage={isClause ? 'Cannot Publish Clause' : 'Cannot Publish Template'}
        />
      );
    }
    return (
      <FormattedMessage
        id={isClause ? 'clause.publish.confirm.title' : 'template.publish.confirm.title'}
        defaultMessage={isClause ? 'Publish Clause' : 'Publish Template'}
      />
    );
  };

  const getModalBody = () => {
    const isClause = templateType === TemplateType.CLAUSE;

    if (hasPendingSuggestions) {
      return (
        <div>
          <p className="mb-3">
            <FormattedMessage
              id={isClause ? 'clause.publish.blocked.message' : 'template.publish.blocked.message'}
              defaultMessage={
                isClause
                  ? 'This clause cannot be published because it has pending suggestions that need to be resolved first.'
                  : 'This template cannot be published because it has pending suggestions that need to be resolved first.'
              }
            />
          </p>
          <p className="text-muted">
            <FormattedMessage
              id="template.publish.blocked.instructions"
              defaultMessage="Please accept or reject all pending suggestions before publishing."
            />
          </p>
        </div>
      );
    }

    return (
      <div>
        <p className="mb-3">Are you sure you want to publish '{templateName}'?</p>
        <p className="text-muted">
          <FormattedMessage
            id={isClause ? 'clause.publish.confirm.details' : 'template.publish.confirm.details'}
            defaultMessage={
              isClause
                ? 'Publishing will make your draft changes available for use in documents. Comments will be removed from the published version.'
                : 'Publishing will make your draft changes available for document creation. Comments will be removed from the published version.'
            }
          />
        </p>
      </div>
    );
  };

  const getFooterButtons = () => {
    if (hasPendingSuggestions) {
      return (
        <Button variant="primary" onClick={handleClose}>
          <FormattedMessage id="common.close" defaultMessage="Close" />
        </Button>
      );
    }

    return (
      <>
        <Button variant="secondary" onClick={handleClose} disabled={isPublishing}>
          <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
        </Button>
        <Button variant="primary" onClick={handlePublish} disabled={isPublishing}>
          {isPublishing ? (
            <FormattedMessage id="template.publishing" defaultMessage="Publishing..." />
          ) : (
            <FormattedMessage
              id={templateType === TemplateType.CLAUSE ? 'clause.publish' : 'template.publish'}
              defaultMessage={templateType === TemplateType.CLAUSE ? 'Publish Clause' : 'Publish Template'}
            />
          )}
        </Button>
      </>
    );
  };

  return (
    <PropdocsModal show={isOpen} setShow={handleClose} size="md" title={getModalTitle()} disableClose={isPublishing}>
      <>
        <div style={{ marginBottom: '1.5rem' }}>{getModalBody()}</div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', paddingTop: '1.5rem' }}>
          {getFooterButtons()}
        </div>
      </>
    </PropdocsModal>
  );
};
